import { useEffect } from "react";

function mk(tag, attr, key) {
  const el = document.createElement(tag);
  el.setAttribute(attr, key);
  return el;
}

// Per-page SEO for this SPA (React 19). Updates the existing head tags in place so there are
// never duplicate <title>/<meta> entries, and restores the previous values on unmount.
export function useSeo({ title, description, canonical, image, jsonLd }) {
  useEffect(() => {
    const head = document.head;
    const restore = [];
    const set = (selector, create, attr, value) => {
      if (value == null) return;
      let el = head.querySelector(selector);
      const existed = !!el;
      if (!el) { el = create(); head.appendChild(el); }
      const prev = el.getAttribute(attr);
      restore.push(() => {
        if (!existed) el.remove();
        else if (prev === null) el.removeAttribute(attr);
        else el.setAttribute(attr, prev);
      });
      el.setAttribute(attr, value);
    };

    const prevTitle = document.title;
    if (title) document.title = title;
    set('meta[name="description"]', () => mk("meta", "name", "description"), "content", description);
    set('meta[property="og:title"]', () => mk("meta", "property", "og:title"), "content", title);
    set('meta[property="og:description"]', () => mk("meta", "property", "og:description"), "content", description);
    set('meta[name="twitter:title"]', () => mk("meta", "name", "twitter:title"), "content", title);
    set('meta[name="twitter:description"]', () => mk("meta", "name", "twitter:description"), "content", description);
    if (canonical) {
      set('meta[property="og:url"]', () => mk("meta", "property", "og:url"), "content", canonical);
      set('link[rel="canonical"]', () => mk("link", "rel", "canonical"), "href", canonical);
    }
    if (image) {
      set('meta[property="og:image"]', () => mk("meta", "property", "og:image"), "content", image);
      set('meta[name="twitter:image"]', () => mk("meta", "name", "twitter:image"), "content", image);
    }

    let script = null;
    if (jsonLd) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "page-jsonld";
      script.text = JSON.stringify(jsonLd);
      head.appendChild(script);
    }

    return () => {
      document.title = prevTitle;
      restore.reverse().forEach((fn) => fn());
      if (script) script.remove();
    };
  }, [title, description, canonical, image, JSON.stringify(jsonLd)]);
}
